import React from 'react';

const Certificates = () => {
  const certificates = [
    {
      title: "What is Data Science",
      issuer: "IBM-Coursera",
      category: "Data Science",
      icon: "fa-chart-line",
      color: "from-blue-500 to-blue-600"
    },
    {
      title: "SQL for Data Science",
      issuer: "IBM-Coursera",
      category: "Database",
      icon: "fa-database",
      color: "from-green-500 to-green-600"
    },
    {
      title: "Python for Data Science and AI",
      issuer: "IBM-Coursera",
      category: "Programming",
      icon: "fa-python",
      color: "from-yellow-500 to-yellow-600"
    },
    {
      title: "Leadership Development Experience",
      issuer: "AIESEC",
      category: "Leadership",
      icon: "fa-users",
      color: "from-purple-500 to-purple-600"
    },
    {
      title: "AI Fundamentals",
      issuer: "IBM",
      category: "Artificial Intelligence",
      icon: "fa-robot",
      color: "from-red-500 to-red-600"
    },
    {
      title: "SQL Project for Beginners",
      issuer: "Great Learning Academy",
      category: "Database",
      icon: "fa-project-diagram",
      color: "from-indigo-500 to-indigo-600"
    },
    {
      title: "Power BI for Beginners",
      issuer: "Simplilearn | SkillUP",
      category: "Business Intelligence",
      icon: "fa-chart-bar",
      color: "from-pink-500 to-pink-600"
    }
  ];

  const getCategoryIcon = (category) => {
    const categoryIcons = {
      "Data Science": "fa-chart-line",
      "Database": "fa-database", 
      "Programming": "fa-code",
      "Leadership": "fa-users",
      "Artificial Intelligence": "fa-robot",
      "Business Intelligence": "fa-chart-bar"
    };
    return categoryIcons[category] || "fa-certificate";
  };

  const getCategoryColor = (category) => {
    const categoryColors = {
      "Data Science": "bg-blue-100 text-blue-800",
      "Database": "bg-green-100 text-green-800",
      "Programming": "bg-yellow-100 text-yellow-800", 
      "Leadership": "bg-purple-100 text-purple-800",
      "Artificial Intelligence": "bg-red-100 text-red-800",
      "Business Intelligence": "bg-pink-100 text-pink-800"
    };
    return categoryColors[category] || "bg-gray-100 text-gray-800";
  };

  return (
    <section id="certificates" className="section-padding bg-white/70 backdrop-blur-md shadow-lg">
      <div className="container-max">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Certifications</h2>
          <div className="w-20 h-1 bg-primary-600 mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Professional certifications and courses that validate my technical expertise and commitment to continuous learning
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {certificates.map((cert, index) => (
            <div 
              key={index}
              className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100 overflow-hidden"
            >
              <div className={`h-2 bg-gradient-to-r ${cert.color}`}></div>
              
              <div className="p-6">
                <div className="text-center mb-4">
                  <div className={`w-16 h-16 bg-gradient-to-r ${cert.color} rounded-full flex items-center justify-center mb-4 mx-auto`}>
                    <i className={`${cert.icon === 'fa-python' ? 'fab' : 'fas'} ${cert.icon} text-2xl text-white`}></i>
                  </div>
                  
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mb-3 ${getCategoryColor(cert.category)}`}>
                    {cert.category}
                  </span>
                </div>
                
                <h3 className="text-lg font-bold text-gray-900 mb-3 text-center leading-tight">
                  {cert.title}
                </h3>
                
                <div className="text-center">
                  <div className="flex items-center justify-center text-gray-600 mb-3">
                    <i className="fas fa-building mr-2 text-sm"></i>
                    <span className="text-sm font-medium">{cert.issuer}</span>
                  </div>
                  
                  <div className="flex items-center justify-center text-gray-500">
                    <i className="fas fa-certificate mr-2 text-sm"></i>
                    <span className="text-xs">Certified</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 bg-gradient-to-r from-primary-50 to-blue-50 rounded-xl p-8">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              <i className="fas fa-trophy text-primary-600 mr-3"></i>
              Certification Highlights
            </h3>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <i className="fas fa-graduation-cap text-3xl text-primary-600"></i>
                </div>
                <h4 className="text-xl font-semibold text-gray-900 mb-2">7+</h4>
                <p className="text-gray-600">Professional Certifications</p>
              </div>
              
              <div className="text-center">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <i className="fas fa-building text-3xl text-primary-600"></i>
                </div>
                <h4 className="text-xl font-semibold text-gray-900 mb-2">5+</h4>
                <p className="text-gray-600">Leading Organizations</p>
              </div>
              
              <div className="text-center">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <i className="fas fa-star text-3xl text-primary-600"></i>
                </div>
                <h4 className="text-xl font-semibold text-gray-900 mb-2">100%</h4>
                <p className="text-gray-600">Completion Rate</p>
              </div>
            </div>
            
            <div className="mt-8">
              <p className="text-gray-700 leading-relaxed max-w-3xl mx-auto">
                These certifications represent my commitment to staying current with industry standards 
                and best practices in data science, artificial intelligence, and software development. 
                Each certification has enhanced my technical skills and provided practical knowledge 
                applicable to real-world projects.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Certificates;
